import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Book, Truck, CreditCard, CheckCircle, ArrowLeft, ChevronRight, Package } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PRICES = { 1: { amount: 59, label: '1 Hardcover Book', desc: 'Perfect for yourself' }, 2: { amount: 89, label: '2 Hardcover Books', desc: 'One for you, one to gift' } };

const STEPS = ['Preview', 'Shipping', 'Payment'];

const HardcoverOrderFlow = () => {
  const { autobiographyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [step, setStep] = useState(0);
  const [autobiography, setAutobiography] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [shipping, setShipping] = useState({ shipping_name: '', street1: '', city: '', state_code: '', country_code: 'GB', postcode: '', phone_number: '', contact_email: '' });

  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    fetchAutobiography();
    if (cancelled) toast({ title: 'Payment Cancelled', description: 'You can try again when ready.', variant: 'destructive' });
  }, [autobiographyId]);

  const fetchAutobiography = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/autobiography/${autobiographyId}`);
      if (res.ok) setAutobiography(await res.json());
      else navigate('/dashboard');
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const handleShippingChange = (e) => setShipping(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCheckout = async () => {
    const required = ['shipping_name', 'street1', 'city', 'country_code', 'postcode', 'contact_email'];
    for (const f of required) {
      if (!shipping[f]?.trim()) { toast({ title: 'Missing Info', description: `Please fill in ${f.replace('_', ' ')}`, variant: 'destructive' }); return; }
    }
    setProcessing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hardcover/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autobiography_id: autobiographyId, quantity, ...shipping }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error(data.detail || 'Checkout failed');
    } catch (err) {
      toast({ title: 'Checkout Error', description: err.message, variant: 'destructive' });
      setProcessing(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
    </div>
  );

  if (!autobiography) return null;

  const price = PRICES[quantity];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4" data-testid="hardcover-order-flow">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Back</span>
        </button>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Turn Your Life Story Into a Beautiful Hardcover Book</h1>
        <p className="text-gray-600">A professionally printed, bound book delivered to your door</p>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto mb-10">
        <div className="flex items-center justify-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${i <= step ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-white/60 text-gray-400'}`} data-testid={`step-indicator-${i}`}>
                {i === 0 && <Book className="w-4 h-4" />}
                {i === 1 && <Truck className="w-4 h-4" />}
                {i === 2 && <CreditCard className="w-4 h-4" />}
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Step 0: Book Preview */}
        {step === 0 && (
          <div className="fade-in" data-testid="step-preview">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Book Mock */}
              <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl flex flex-col items-center justify-center min-h-[400px] relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 11px)' }} />
                <div className="bg-white rounded-lg p-6 w-48 sm:w-56 shadow-lg transform rotate-[-2deg] relative z-10">
                  <div className="border-b-2 border-pink-200 pb-3 mb-3">
                    <h3 className="text-sm font-bold text-gray-800 leading-tight">{autobiography.title}</h3>
                    <p className="text-xs text-gray-500 italic mt-1">{autobiography.subtitle}</p>
                  </div>
                  <p className="text-[8px] text-gray-400 leading-relaxed line-clamp-6">{autobiography.content?.substring(0, 200)}...</p>
                  <div className="mt-3 pt-2 border-t border-gray-100 text-[7px] text-gray-400 text-center">Page 1</div>
                </div>
                <div className="mt-4 text-white/60 text-xs text-center z-10">
                  {autobiography.word_count} words &middot; {autobiography.chapters} chapters
                </div>
              </div>

              {/* Book Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{autobiography.title}</h3>
                  <p className="text-gray-500 italic">{autobiography.subtitle}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700">What's Included:</h4>
                  {['Professional hardcover binding', 'Title page & copyright page', `${autobiography.chapters} beautifully formatted chapters`, 'Page numbers throughout', autobiography.photos?.length ? `${autobiography.photos.length} photos with captions` : null, 'Elegant dark cover with gold accents'].filter(Boolean).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Quantity Selection */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700">Choose Quantity:</h4>
                  {Object.entries(PRICES).map(([q, p]) => (
                    <button key={q} onClick={() => setQuantity(Number(q))}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${Number(q) === quantity ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      data-testid={`quantity-${q}`}
                    >
                      <div className="flex justify-between items-center">
                        <div><span className="font-semibold text-gray-800">{p.label}</span><p className="text-xs text-gray-500 mt-0.5">{p.desc}</p></div>
                        <span className="text-2xl font-bold text-gray-800">£{p.amount}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <Button onClick={() => setStep(1)} className="w-full btn-primary text-white py-5 rounded-full border-0 text-base font-semibold" data-testid="continue-to-shipping">
                  Continue to Shipping <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Shipping */}
        {step === 1 && (
          <div className="fade-in max-w-2xl mx-auto" data-testid="step-shipping">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"><Truck className="w-5 h-5 text-purple-500" /> Shipping Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Full Name *</Label><Input name="shipping_name" value={shipping.shipping_name} onChange={handleShippingChange} placeholder="John Smith" className="mt-1 input-focus" data-testid="input-name" /></div>
                <div className="md:col-span-2"><Label>Email *</Label><Input name="contact_email" value={shipping.contact_email} onChange={handleShippingChange} placeholder="john@example.com" type="email" className="mt-1 input-focus" data-testid="input-email" /></div>
                <div className="md:col-span-2"><Label>Street Address *</Label><Input name="street1" value={shipping.street1} onChange={handleShippingChange} placeholder="123 Main Street" className="mt-1 input-focus" data-testid="input-street" /></div>
                <div><Label>City *</Label><Input name="city" value={shipping.city} onChange={handleShippingChange} placeholder="London" className="mt-1 input-focus" data-testid="input-city" /></div>
                <div><Label>Postcode *</Label><Input name="postcode" value={shipping.postcode} onChange={handleShippingChange} placeholder="SW1A 1AA" className="mt-1 input-focus" data-testid="input-postcode" /></div>
                <div><Label>Country Code *</Label><Input name="country_code" value={shipping.country_code} onChange={handleShippingChange} placeholder="GB" className="mt-1 input-focus" data-testid="input-country" /></div>
                <div><Label>State/Region</Label><Input name="state_code" value={shipping.state_code} onChange={handleShippingChange} placeholder="Optional" className="mt-1 input-focus" data-testid="input-state" /></div>
                <div className="md:col-span-2"><Label>Phone Number</Label><Input name="phone_number" value={shipping.phone_number} onChange={handleShippingChange} placeholder="+44 7700 900000" className="mt-1 input-focus" data-testid="input-phone" /></div>
              </div>

              {/* Order Summary */}
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5">
                <h4 className="font-semibold text-gray-800 mb-3">Order Summary</h4>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>{PRICES[quantity].label}</span><span>£{PRICES[quantity].amount}</span></div>
                <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Shipping</span><span>Included</span></div>
                <div className="border-t pt-2 flex justify-between font-bold text-gray-800"><span>Total</span><span>£{PRICES[quantity].amount}</span></div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1 py-5 rounded-full" data-testid="back-to-preview">Back</Button>
                <Button onClick={() => setStep(2)} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="continue-to-payment">
                  Continue to Payment <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Payment Confirmation */}
        {step === 2 && (
          <div className="fade-in max-w-2xl mx-auto" data-testid="step-payment">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"><CreditCard className="w-5 h-5 text-purple-500" /> Confirm Your Order</h3>

              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Book</p>
                  <p className="font-semibold text-gray-800">{autobiography.title}</p>
                  <p className="text-sm text-gray-600">{PRICES[quantity].label}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Shipping to</p>
                  <p className="font-semibold text-gray-800">{shipping.shipping_name}</p>
                  <p className="text-sm text-gray-600">{shipping.street1}, {shipping.city}, {shipping.postcode}, {shipping.country_code}</p>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                  <div className="flex justify-between font-bold text-lg text-gray-800"><span>Total</span><span>£{PRICES[quantity].amount}</span></div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 py-5 rounded-full" data-testid="back-to-shipping">Back</Button>
                <Button onClick={handleCheckout} disabled={processing} className="flex-1 btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="pay-now-btn">
                  {processing ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : <><Package className="w-4 h-4 mr-2" /> Pay £{PRICES[quantity].amount} & Order</>}
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">Secure payment via Stripe. Your book will be printed and shipped within 7-10 business days.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HardcoverOrderFlow;
